import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { match, none, Option, some } from '../../../../shared/functions/functional';
import { FieldTree, FormField, SchemaPathTree } from '@angular/forms/signals';
import { Fuse_BS3036Dto, InDto, MceApiService } from '../../../../core/mceapi.client.generated';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Load } from '../../load/load.model';
import { rxResource } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';

export interface FuseBS3036 {
  assemblyGuid: string,
  rating: number,
  autoRating: boolean,
}

export function buildFuseBS3036Schema(path: SchemaPathTree<FuseBS3036>) { }

export function createFuseBS3036(dto: Option<Fuse_BS3036Dto>) {
  return signal<FuseBS3036>(
    match(
      () => ({ assemblyGuid: '', rating: 0, autoRating: true }),
      (x: Fuse_BS3036Dto): FuseBS3036 => {
        if (x.assemblyGuid === undefined || x.rating === undefined || x.autoRating === undefined) {
          throw new Error('BS 3036 fuse DTO is missing required values.');
        }

        return {
          assemblyGuid: x.assemblyGuid,
          rating: x.rating,
          autoRating: x.autoRating,
        };
      })(dto));
}

export function createFuseBS3036Dto(model: FieldTree<FuseBS3036>) {
  return signal<Option<Fuse_BS3036Dto>>(
    model.assemblyGuid().value() === '' || model.rating().value() === 0
      ? none
      : some({
          assemblyGuid: model.assemblyGuid().value(),
          rating: model.rating().value(),
          autoRating: model.autoRating().value(),
        }));
}

@Component({
  selector: 'protection-fuse-bs3036',
  imports: [FormField, MatSelectModule, MatSlideToggleModule],
  templateUrl: './fuse-bs3036.component.html',
})
export class FuseBs3036Component {

  readonly form = input.required<FieldTree<FuseBS3036>>();
  readonly load = input.required<FieldTree<Load>>();

  private readonly mceApiService = inject(MceApiService);

  framesRx = rxResource({
    params: () => ({}),
    stream: () =>
      this.mceApiService.v1ProtectionFrames('Fuse_BS3036')
  });
  frames = computed(() => this.framesRx.value() ?? []);

  ratingsRx = rxResource({
    params: () => ({ assemblyGuid: this.form().assemblyGuid().value() }),
    stream: ({ params }) =>
      params.assemblyGuid === ''
        ? of([])
        : this.mceApiService.v1ProtectionRatings('Fuse_BS3036', params.assemblyGuid)
  });
  ratings = computed(() => this.ratingsRx.value() ?? []);

  autoRatingRx = rxResource({
    params: () => ({ assemblyGuid: this.form().assemblyGuid().value(), ib: this.load().ib().value() }),
    stream: ({ params }) =>
      params.assemblyGuid === ''
        ? of({ i: 0 })
        : this.mceApiService.v1ProtectionRatingsAuto('Fuse_BS3036', params.assemblyGuid, params.ib)
  });
  autoRating = computed<InDto>(() => this.autoRatingRx.value() ?? { i: 0 });

  setAutoRating = effect(() => {
    if (!this.form().autoRating().value()) return;
    const autoRating = this.autoRating().i;
    if (autoRating === undefined || autoRating === 0) return;

    if (autoRating !== this.form().rating().value())
      this.form().rating().value.set(autoRating);
  });


  /// Ensure the frames has a valid selection.
  frameToValid = effect(() => {
    const frameGuid = this.frames()[0]?.value;
    if (frameGuid !== undefined && this.form().assemblyGuid().value() === '')
      this.form().assemblyGuid().value.set(frameGuid);
  });

}
